/* eslint-disable */
// TODO: eslint disable in place to avoid errors regarding unused variables
// that are thrown due to sections of code being commented out.  Remove
// once chart conversion work is completed.
// Modules:
import React, { useState, useEffect, useContext } from 'react';

// Config:
import config from 'config/config';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Components:
import { useErrorHandler } from 'react-error-boundary';
import { handleFetchError } from 'utilities/utils';
import TrendLine from '../Charts/TrendLine';
import HorizontalBar from '../Charts/HorizontalBar';
import BarLineMix from '../Charts/BarLineMix';

// Styles:
import muiTheme from '../../theme';

// Style:
const styles = {
    '&.MuiFormControl-root': {
        margin: '0 auto',
    },
    '&.MuiAlert-root': {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    '& .MuiMenuItem-root': {
        color: 'red',
    },
    '&.MuiButton-root': {
        borderRight: '1px solid rgb(99, 167, 220) !important',
        minHeight: 'fit-content',
        margin: '0.5em 0',
        width: 'fit-content',
    },
    '&.MuiIconButton-root': {
        margin: 0,
    },
    '&.MuiButtonGroup-root': {
        margin: '0.7rem',
        maxWidth: '85%',
    },
    '&.MuiButton-contained': {
        background: muiTheme.palette.lightBlue.main,
        color: 'white',
        '&:hover': {
            background: muiTheme.palette.secondary.dark,
        },
    },
    '&.MuiButton-outlined': {
        color: muiTheme.palette.lightBlue.main,
        '&:hover': {
            background: muiTheme.palette.lightGray.main,
        },
    },
};

// Functions (do not use props or state):

// Sorts fetched data alphabetically or numerically by axis specified
const sortByAxis = (items, axis, baseline) => {
    // Copy items to avoid mutating state:
    const sortedItems = items.sort((item, nextItem) => {
        if (item[axis] !== baseline && nextItem[axis] === baseline) {
            return 1;
        }
        if (item[axis] === baseline && nextItem[axis] !== baseline) {
            return -1;
        }
        if (item[axis] === nextItem[axis]) {
            return 0;
        }
        return item[axis] > nextItem[axis] ? 1 : -1;
    });
    return sortedItems;
};

const checkIfNull = (datasetArray, filterKey) =>
    datasetArray
        .filter((dataset) => !dataset[filterKey])
        .map((dataset) => dataset.data)
        .flat()
        .every((dataPoint) => dataPoint === null);

const ChartContent = ({
    counties,
    setChartDemographicContent,
    setTableData,
    setMissingSuppressedModalOpen,
    topicDataYears,
    stratification,
    setStratification,
    currentData,
    setCurrentData,
    selection,
    setSelection,
    setChartInfoModalOpen,
}) => {
    // // State // //
    const [dataUnavailable, setDataUnavailable] = useState(false);

    // // Context // //
    const topicConfigContext = useContext(TopicConfigContext);

    // // Hooks // //
    const handleError = useErrorHandler();

    // // Methods // //
    const sanitizeData = (rawJson) =>
        [...rawJson].map((dataPoint) => {
            const copy = { ...dataPoint };
            Object.keys(copy).forEach((key) => {
                if (
                    copy[key] === config.suppressed ||
                    copy[key] === config.missing
                ) {
                    copy[key] = null;
                }
            });
            return copy;
        });

    const handleDropdownSelect = (e) => {
        // Constructing this object for the Chart with the key 'MDCode' to mirror how data is passed to the 'CommunityProfile' component from the Map.
        const popupFeature = {
            properties: {
                MDCode: e.target.value,
            },
        };
        // setChartDemographicContent(popupFeature); TODO: Uncomment this out when work on demo content starts.
        setSelection(e.target.value);
    };

    // // Effects // //

    // Main use: fetching data when selection changes
    useEffect(() => {
        const formatTableData = (json) => {
            const {
                tableTitle,
                chartSettings: {
                    defaultSetNames,
                    chartXAxisField,
                    baselineJurisdictions: baseline,
                    infoID,
                    infoTitle,
                },
            } = topicConfigContext.tab;

            let columnHeaders = [
                ...topicConfigContext.tab.chartSettings.columnHeaders,
            ];

            // Limit column headers if chart both has a baseline and user has selected it
            // Second part of the conditional checks if there are two baseline datasets, which would be
            // housed in an array.  If so, than we are working with both nationWide and stateWide data.
            if (baseline && baseline.includes(selection)) {
                columnHeaders = columnHeaders.filter(
                    (header) =>
                        defaultSetNames.includes(header.field) || // Keep baseline header and the x axis, removing comparison column
                        header.field === chartXAxisField
                );
            }
            // Adds in only county column, stratification columns are handled individually:
            else {
                columnHeaders = columnHeaders.filter(
                    (header) => !header.stratification
                );
            }

            // Appending stratification columns, logic below is to add them between county and statewide columns for more relevant grouping:
            if (stratification) {
                const stratificationColumns = topicConfigContext.tab.chartSettings.columnHeaders.filter(
                    (header) => stratification === header.stratification
                );

                const stateColumnIndex = columnHeaders.findIndex(
                    (item) => item.field === 'stateRate'
                );

                columnHeaders.splice(
                    stateColumnIndex,
                    0,
                    ...stratificationColumns
                );
            }

            const sortedFeatures = sortByAxis(
                [...json],
                chartXAxisField,
                baseline
            );

            const formattedTableData = {
                id: infoID,
                info: {
                    infoTitle,
                    columnHeaders,
                    tableTitle,
                },
                selection,
                features: sortedFeatures,
            };

            setTableData([formattedTableData]);
        };

        const getQueryString = (selected) => {
            const queryString = topicConfigContext.tab.chartSettings.urlParams.reduce(
                (
                    urlParamString,
                    currentParamObject,
                    currentIndex,
                    paramsArr
                ) => {
                    const { param } = currentParamObject;

                    // If there is no 'value' key present in
                    // the object, it is the 'jurisdiction' which
                    // updates dynamically by the selector.
                    const value = currentParamObject.value
                        ? currentParamObject.value
                        : selected;

                    const additionalParams =
                        currentIndex + 1 !== paramsArr.length ? '&' : '';

                    return `${urlParamString}${param}=${value}${additionalParams}`;
                },
                ''
            );

            return queryString ? `?${queryString}` : '';
        };

        if (selection) {
            setChartDemographicContent({ properties: { MDCode: selection } });
            let baseURL = topicConfigContext.tab.chartSettings.url;
            const fetchData = (url) => {
                fetch(url)
                    .then(handleFetchError)
                    .then((res) => res.json())
                    .then((resJson) => {
                        const sanitizedData = sanitizeData([...resJson]);
                        setCurrentData(sanitizedData);
                        formatTableData(resJson);
                    })
                    .catch(handleError);
            };

            baseURL = `${
                topicConfigContext.tab.chartSettings.url
            }${getQueryString(selection)}`;

            setDataUnavailable(false);
            fetchData(baseURL);
        }
    }, [
        selection,
        topicConfigContext.tab.chartSettings.url,
        topicConfigContext.tab,
        handleError,
        setTableData,
        stratification,
        setStratification,
        setCurrentData,
        setChartDemographicContent,
    ]);

    // Cleans up stratification when moving away from Chart content type inside useEffect return block:
    useEffect(
        () => () => {
            setStratification();
        },
        [topicConfigContext.tab.contentType, setStratification]
    );

    // // Render // //
    // eslint-disable-next-line no-nested-ternary
    return topicConfigContext.tab.chartSettings.chartType === 'trendline' ? (
        <TrendLine
            key={topicConfigContext.tab.tabPath} // Note: key is used to force an unmount/mount when switching between buttons which both contain a Trendline component (otherwise cleanup effect will not run)
            counties={counties}
            json={currentData}
            selection={selection}
            stratification={stratification}
            setStratification={setStratification}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            styles={styles}
            handleDropdownSelect={handleDropdownSelect}
            checkIfNull={checkIfNull}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
        />
    ) : // eslint-disable-next-line no-nested-ternary
    topicConfigContext.tab.chartSettings.chartType === 'horizontalBar' ? (
        <HorizontalBar
            topicDataYears={topicDataYears}
            json={currentData}
            selection={selection}
            sortByAxis={sortByAxis}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            handleDropdownSelect={handleDropdownSelect}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
        />
    ) : // eslint-disable-next-line no-nested-ternary
    topicConfigContext.tab.chartSettings.chartType === 'mixed' ? (
        <BarLineMix
            counties={counties}
            json={currentData}
            sortByAxis={sortByAxis}
            selection={selection}
            handleDropdownSelect={handleDropdownSelect}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
        />
    ) : null;
};

export default ChartContent;
